import { useQuery } from '@tanstack/react-query'
import { AdminOrderService } from '@/shared/api'
import type { OrderListRequest } from '@/shared/api'

// Ключи React Query для админского раздела заказов
const QUERY_KEYS = {
  list: (params?: OrderListRequest) => ['orders', 'list', params] as const,
  details: (id: number) => ['orders', 'details', id] as const,
  history: (id: number) => ['orders', 'history', id] as const,
  filters: () => ['orders', 'filters'] as const,
  couriers: (date: number) => ['orders', 'couriers', date] as const,
  load: (id: number, date: number) => ['orders', 'load', id, date] as const,
}

/**
 * Список заказов.
 * Дата фильтра (dateFrom/dateTo) — Unix-секунды начала дня МСК;
 * input type=date конвертируется на странице списка.
 * `enabled` позволяет не отправлять запрос при заведомо некорректных фильтрах.
 */
export function useOrders(
  params?: OrderListRequest,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: QUERY_KEYS.list(params),
    queryFn: () => AdminOrderService.getOrders(params),
    staleTime: 30 * 1000,
    enabled: options?.enabled !== false,
  })
}

/** Карточка заказа (read-only) */
export function useOrderDetails(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.details(id),
    queryFn: () => AdminOrderService.getOrderDetails(id),
    staleTime: 30 * 1000,
    enabled: options?.enabled !== false && id > 0,
  })
}

/** История изменений заказа */
export function useOrderHistory(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.history(id),
    queryFn: () => AdminOrderService.getOrderHistory(id),
    staleTime: 30 * 1000,
    enabled: options?.enabled !== false && id > 0,
  })
}

/** Справочники фильтров списка заказов */
export function useOrderFilters(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: QUERY_KEYS.filters(),
    queryFn: () => AdminOrderService.getOrderFilters(),
    staleTime: 60 * 1000,
    enabled: options?.enabled !== false,
  })
}

/**
 * Активные курьеры; с датой — нагрузка на этот день.
 * Без даты нагрузка не запрашивается (в ключе — 0).
 */
export function useOrderCouriers(date?: number | null) {
  const day = date ?? 0

  return useQuery({
    queryKey: QUERY_KEYS.couriers(day),
    queryFn: () => AdminOrderService.getOrderCouriers(day > 0 ? day : undefined),
    staleTime: 60 * 1000,
  })
}

/**
 * Нагрузка курьера на дату.
 * Запрос выполняется только при наличии курьера и даты.
 */
export function useCourierLoad(courierId?: number | null, date?: number | null) {
  const id = courierId ?? 0
  const day = date ?? 0

  return useQuery({
    queryKey: QUERY_KEYS.load(id, day),
    queryFn: () => AdminOrderService.getCourierLoad(id, day),
    staleTime: 30 * 1000,
    enabled: id > 0 && day > 0,
  })
}
